/**
 * When the mirror runs. Section: "push the queue on app background, on app
 * foreground, and at most once every few minutes while in use. Do not fire a
 * request per tick."
 *
 * Nothing here is on the critical path of a tick. Every trigger is fire-and-forget
 * and every failure is swallowed into the mirror's own recorded state, which the
 * Data screen reads. A tick never waits for this.
 */
import { Mirror, backoffMs } from './mirror.ts';
import type { MirrorState } from './mirror.ts';

export const IDLE_INTERVAL_MS = 3 * 60_000;

export class MirrorScheduler {
  private readonly mirror: Mirror;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastRun = 0;
  private listeners = new Set<(s: MirrorState) => void>();
  private detach: Array<() => void> = [];

  constructor(mirror: Mirror) { this.mirror = mirror; }

  onChange(fn: (s: MirrorState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  start(): void {
    if (this.timer) return;

    const onVisibility = () => {
      // Both directions matter: foreground catches a reconnect, background catches
      // the ticks made during a session that is about to end.
      void this.run(document.visibilityState === 'visible' ? 'foreground' : 'background');
    };
    const onPageHide = () => { void this.run('pagehide'); };
    const onOnline = () => { void this.run('online'); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('online', onOnline);
    this.detach = [
      () => document.removeEventListener('visibilitychange', onVisibility),
      () => window.removeEventListener('pagehide', onPageHide),
      () => window.removeEventListener('online', onOnline),
    ];

    this.timer = setInterval(() => {
      if (document.visibilityState === 'visible') void this.run('interval');
    }, IDLE_INTERVAL_MS);

    void this.run('start');
  }

  stop(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    for (const off of this.detach) off();
    this.detach = [];
  }

  /** Manual trigger from the Data screen. Ignores backoff, because the user asked. */
  async runNow(): Promise<MirrorState> {
    const s = await this.mirror.drain(20, true);
    this.lastRun = Date.now();
    for (const fn of this.listeners) fn(s);
    return s;
  }

  private async run(reason: string): Promise<void> {
    const now = Date.now();
    const state = await this.mirror.state().catch(() => null);
    if (state) {
      const wait = backoffMs(state.consecutiveFailures);
      if (wait > 0 && state.lastAttemptAt !== null && now - state.lastAttemptAt < wait) return;
    }
    // Never more than one push per interval, whatever fires.
    if (reason === 'interval' && now - this.lastRun < IDLE_INTERVAL_MS) return;
    this.lastRun = now;
    const s = await this.mirror.drain(20, reason !== 'interval').catch(() => null);
    if (s) for (const fn of this.listeners) fn(s);
  }
}
